import { useState, useEffect } from "react";
import axios from "axios";
import { useAuth } from "../App";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Switch } from "../components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "../components/ui/tooltip";
import { motion, AnimatePresence } from "framer-motion";
import { 
  AlertTriangle, Bell, EyeOff, Mail, Calendar, CheckCircle, Info, Euro, 
  RefreshCw, Settings, Send, Clock, TrendingUp, FileText, History, 
  AlertCircle, ChevronRight, Eye
} from "lucide-react";
import { toast } from "sonner";
import MissingVsLastMonthButton from "../components/MissingVsLastMonthButton";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const formatCurrency = (value) => {
  return new Intl.NumberFormat('fr-FR', {
    style: 'currency',
    currency: 'EUR',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0
  }).format(value || 0);
};

const formatDate = (dateStr) => {
  if (!dateStr) return '-';
  return new Date(dateStr).toLocaleDateString('fr-FR', { timeZone: 'Europe/Paris' });
};

const generateMonthOptions = () => {
  const options = [];
  const now = new Date();
  for (let i = 0; i < 12; i++) {
    const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
    options.push({
      value: `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`,
      label: d.toLocaleDateString('fr-FR', { month: 'long', year: 'numeric', timeZone: 'Europe/Paris' })
    });
  }
  return options;
};

const PriorityBadge = ({ priority }) => {
  if (priority === 'high') {
    return <Badge className="bg-red-100 text-red-700 border-red-200">Haute</Badge>;
  } else if (priority === 'medium') {
    return <Badge className="bg-amber-100 text-amber-700 border-amber-200">Moyenne</Badge>;
  }
  return <Badge className="bg-gray-100 text-gray-600 border-gray-200">Basse</Badge>;
};

export default function Vigilance() {
  const { token } = useAuth();
  const [alerts, setAlerts] = useState([]);
  const [stats, setStats] = useState({});
  const [loading, setLoading] = useState(true);
  const [initialLoading, setInitialLoading] = useState(true);
  const [selectedMonth, setSelectedMonth] = useState(null);
  const [sendingNotification, setSendingNotification] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [settings, setSettings] = useState({
    sender_email: "facturation@betaye.fr",
    recipient_email: "",
    cc_email: "",
    notification_day: 10,
    enabled: false
  });
  const [savingSettings, setSavingSettings] = useState(false);
  const [notificationHistory, setNotificationHistory] = useState([]);
  const [activeTab, setActiveTab] = useState("alerts");

  const monthOptions = generateMonthOptions();

  // On mount, fetch the default month (most recent with alerts)
  useEffect(() => {
    const fetchDefaultMonth = async () => {
      try {
        const headers = { Authorization: `Bearer ${token}` };
        const res = await axios.get(`${API}/vigilance/default-month`, { headers });
        setSelectedMonth(res.data.month);
      } catch (error) {
        // Fallback to current month
        const now = new Date();
        setSelectedMonth(`${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`);
      } finally {
        setInitialLoading(false);
      }
    };
    
    if (token) {
      fetchDefaultMonth();
      fetchSettings();
    }
  }, [token]);

  // Fetch alerts when month changes
  useEffect(() => {
    if (selectedMonth) {
      fetchAlerts();
    }
  }, [selectedMonth]);

  const fetchAlerts = async () => {
    try {
      setLoading(true);
      const headers = { Authorization: `Bearer ${token}` };
      const res = await axios.get(`${API}/vigilance?month=${selectedMonth}`, { headers });
      setAlerts(res.data.alerts || []);
      setStats({
        total_alerts: res.data.total_alerts,
        high_priority: res.data.high_priority,
        total_at_risk_ht: res.data.total_at_risk_ht,
        recurring_invoices_detected: res.data.recurring_invoices_detected,
        prev_month: res.data.prev_month,
        prev_month_label: res.data.prev_month_label,
        two_months_ago: res.data.two_months_ago,
        two_months_ago_label: res.data.two_months_ago_label,
        month_label: res.data.month_label
      });
    } catch (error) {
      console.error("Failed to fetch alerts:", error);
      toast.error("Erreur lors du chargement des alertes");
    } finally {
      setLoading(false);
    }
  };

  const fetchSettings = async () => {
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const res = await axios.get(`${API}/vigilance/settings`, { headers });
      setSettings(res.data);
    } catch (error) {
      console.error("Failed to fetch settings:", error);
    }
  };

  const fetchHistory = async () => {
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const res = await axios.get(`${API}/vigilance/history`, { headers });
      setNotificationHistory(res.data.notifications || []);
    } catch (error) {
      console.error("Failed to fetch history:", error);
    }
  };

  const handleIgnore = async (alert) => {
    try {
      const headers = { Authorization: `Bearer ${token}` };
      await axios.post(
        `${API}/vigilance/ignore?client_name=${encodeURIComponent(alert.client_name)}&month=${selectedMonth}&amount=${alert.expected_amount_ht}`,
        {},
        { headers }
      );
      toast.success("Alerte ignorée pour ce mois");
      fetchAlerts();
    } catch (error) {
      toast.error("Erreur lors de l'ignorance");
    }
  };

  const handleSaveSettings = async () => {
    if (!settings.sender_email.endsWith("@betaye.fr")) {
      toast.error("L'adresse d'envoi doit se terminer par @betaye.fr");
      return;
    }
    
    setSavingSettings(true);
    try {
      const headers = { Authorization: `Bearer ${token}` };
      await axios.post(`${API}/vigilance/settings`, settings, { headers });
      toast.success("Paramètres enregistrés");
      setSettingsOpen(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erreur lors de l'enregistrement");
    } finally {
      setSavingSettings(false);
    }
  };

  const handleSendNotification = async () => {
    if (!settings.recipient_email) {
      toast.error("Veuillez d'abord configurer l'adresse destinataire dans les paramètres");
      setSettingsOpen(true);
      return;
    }
    
    setSendingNotification(true);
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const res = await axios.post(`${API}/vigilance/send-notification`, {}, { headers });
      if (res.data.success) {
        if (res.data.alerts_sent > 0) {
          toast.success(`Email envoyé avec ${res.data.alerts_sent} alerte(s)`);
        } else {
          toast.info("Aucune alerte à envoyer");
        }
        fetchHistory();
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erreur lors de l'envoi");
    } finally {
      setSendingNotification(false);
    }
  };

  // Show loading while fetching default month
  if (initialLoading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-center">
          <RefreshCw className="w-8 h-8 animate-spin mx-auto text-blue-500 mb-4" />
          <p className="text-muted-foreground">Recherche des anomalies...</p>
        </div>
      </div>
    );
  }

  return (
    <TooltipProvider>
      <div className="space-y-6" data-testid="vigilance-page">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-foreground flex items-center gap-2">
              <AlertTriangle className="w-6 h-6 text-amber-500" />
              Vigilance Facturation
            </h1>
            <p className="text-muted-foreground text-sm mt-1">
              Détection intelligente des factures récurrentes manquantes
            </p>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <MissingVsLastMonthButton token={token} month={selectedMonth} />
            <Button 
              variant="outline" 
              size="sm" 
              onClick={fetchAlerts}
              disabled={loading}
            >
              <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
              Actualiser
            </Button>
            <Select value={selectedMonth || ''} onValueChange={setSelectedMonth}>
              <SelectTrigger className="w-48" data-testid="month-select">
                <Calendar className="w-4 h-4 mr-2 text-muted-foreground" />
                <SelectValue placeholder="Sélectionner un mois" />
              </SelectTrigger>
              <SelectContent>
                {monthOptions.map(m => (
                  <SelectItem key={m.value} value={m.value}>
                    {m.label}
                    {/* Show alert indicator for months with issues */}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Button 
              variant="outline" 
              size="sm" 
              onClick={() => setSettingsOpen(true)}
            >
              <Settings className="w-4 h-4 mr-2" />
              Paramètres
            </Button>
          </div>
        </div>

        {/* Explanation Banner */}
        <Card className="bg-gradient-to-r from-blue-50 to-indigo-50 border-blue-200">
          <CardContent className="p-4">
            <div className="flex items-start gap-3">
              <div className="p-2 bg-blue-100 rounded-lg">
                <Info className="w-5 h-5 text-blue-600" />
              </div>
              <div className="text-sm text-blue-900">
                <p className="font-semibold mb-1">Comment fonctionne la vigilance intelligente ?</p>
                <p className="text-blue-800">
                  Le système analyse les factures des <strong>2 derniers mois</strong> ({stats.two_months_ago_label} et {stats.prev_month_label}). 
                  Si un client a été facturé le même montant ces 2 mois et n'a <strong>pas encore été facturé ce mois-ci</strong>, 
                  une alerte apparaît. Elle disparaît automatiquement dès que la facture est saisie.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <Card className={stats.total_alerts > 0 ? "border-red-200 bg-red-50" : "border-green-200 bg-green-50"}>
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Alertes</p>
                  <p className={`text-2xl font-bold ${stats.total_alerts > 0 ? 'text-red-600' : 'text-green-600'}`}>
                    {stats.total_alerts || 0}
                  </p>
                </div>
                {stats.total_alerts > 0 ? (
                  <AlertTriangle className="w-8 h-8 text-red-400" />
                ) : (
                  <CheckCircle className="w-8 h-8 text-green-400" />
                )}
              </div>
            </CardContent>
          </Card>

          <Card className="border-amber-200 bg-amber-50">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Priorité haute</p>
                  <p className="text-2xl font-bold text-amber-600">{stats.high_priority || 0}</p>
                </div>
                <AlertCircle className="w-8 h-8 text-amber-400" />
              </div>
            </CardContent>
          </Card>

          <Card className="border-blue-200 bg-blue-50">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">CA à risque</p>
                  <p className="text-2xl font-bold text-blue-600">{formatCurrency(stats.total_at_risk_ht)}</p>
                </div>
                <Euro className="w-8 h-8 text-blue-400" />
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Récurrences détectées</p>
                  <p className="text-2xl font-bold text-foreground">{stats.recurring_invoices_detected || 0}</p>
                </div>
                <TrendingUp className="w-8 h-8 text-gray-400" />
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={(v) => { setActiveTab(v); if (v === 'history') fetchHistory(); }}>
          <div className="flex items-center justify-between">
            <TabsList>
              <TabsTrigger value="alerts" className="flex items-center gap-2">
                <AlertTriangle className="w-4 h-4" />
                Alertes ({stats.total_alerts || 0})
              </TabsTrigger>
              <TabsTrigger value="history" className="flex items-center gap-2">
                <History className="w-4 h-4" />
                Historique envois
              </TabsTrigger>
            </TabsList>
            
            {activeTab === 'alerts' && stats.total_alerts > 0 && (
              <Button 
                onClick={handleSendNotification}
                disabled={sendingNotification}
                className="bg-blue-600 hover:bg-blue-700"
              >
                {sendingNotification ? (
                  <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <Send className="w-4 h-4 mr-2" />
                )}
                Envoyer notification
              </Button>
            )}
          </div>

          {/* Alerts Tab */}
          <TabsContent value="alerts" className="mt-4">
            {loading ? (
              <Card>
                <CardContent className="p-8 text-center">
                  <RefreshCw className="w-8 h-8 animate-spin mx-auto text-muted-foreground" />
                  <p className="mt-2 text-muted-foreground">Chargement...</p>
                </CardContent>
              </Card>
            ) : alerts.length === 0 ? (
              <Card className="border-green-200">
                <CardContent className="p-8 text-center">
                  <CheckCircle className="w-12 h-12 mx-auto text-green-500 mb-4" />
                  <h3 className="text-lg font-semibold text-foreground">Aucune alerte</h3>
                  <p className="text-muted-foreground mt-1">
                    Toutes les factures récurrentes ont été émises pour {stats.month_label}
                  </p>
                </CardContent>
              </Card>
            ) : (
              <Card>
                <Table>
                  <TableHeader>
                    <TableRow className="bg-gray-50">
                      <TableHead className="font-semibold">Client</TableHead>
                      <TableHead className="font-semibold text-right">Montant attendu</TableHead>
                      <TableHead className="font-semibold text-center">Priorité</TableHead>
                      <TableHead className="font-semibold">Dernière facture ({stats.prev_month_label})</TableHead>
                      <TableHead className="font-semibold">Facture {stats.two_months_ago_label}</TableHead>
                      <TableHead className="font-semibold text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    <AnimatePresence>
                      {alerts.map((alert, i) => (
                        <motion.tr
                          key={`${alert.client_name}-${alert.rounded_amount}`}
                          initial={{ opacity: 0, y: 10 }}
                          animate={{ opacity: 1, y: 0 }}
                          exit={{ opacity: 0, x: -100 }}
                          transition={{ delay: i * 0.05 }}
                          className="hover:bg-gray-50"
                        >
                          <TableCell>
                            <div className="flex items-center gap-2">
                              <div className="w-2 h-2 rounded-full bg-red-500 animate-pulse" />
                              <span className="font-medium">{alert.client_name}</span>
                            </div>
                          </TableCell>
                          <TableCell className="text-right">
                            <span className="font-mono font-bold text-lg">
                              {formatCurrency(alert.expected_amount_ht)}
                            </span>
                            <span className="text-muted-foreground text-sm ml-1">HT</span>
                          </TableCell>
                          <TableCell className="text-center">
                            <PriorityBadge priority={alert.priority} />
                          </TableCell>
                          <TableCell>
                            <div className="text-sm">
                              <p className="font-medium">{alert.last_invoice_m1?.numero || '-'}</p>
                              <p className="text-muted-foreground">
                                {formatDate(alert.last_invoice_m1?.date)} • {formatCurrency(alert.last_invoice_m1?.amount_ht)}
                              </p>
                            </div>
                          </TableCell>
                          <TableCell>
                            <div className="text-sm">
                              <p className="font-medium">{alert.invoice_m2?.numero || '-'}</p>
                              <p className="text-muted-foreground">
                                {formatDate(alert.invoice_m2?.date)} • {formatCurrency(alert.invoice_m2?.amount_ht)}
                              </p>
                            </div>
                          </TableCell>
                          <TableCell className="text-right">
                            <div className="flex items-center justify-end gap-2">
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => handleIgnore(alert)}
                                    className="text-muted-foreground hover:text-foreground"
                                  >
                                    <EyeOff className="w-4 h-4" />
                                  </Button>
                                </TooltipTrigger>
                                <TooltipContent>Ignorer pour ce mois</TooltipContent>
                              </Tooltip>
                            </div>
                          </TableCell>
                        </motion.tr>
                      ))}
                    </AnimatePresence>
                  </TableBody>
                </Table>
              </Card>
            )}
          </TabsContent>

          {/* History Tab */}
          <TabsContent value="history" className="mt-4">
            <Card>
              {notificationHistory.length === 0 ? (
                <CardContent className="p-8 text-center">
                  <History className="w-12 h-12 mx-auto text-gray-300 mb-4" />
                  <h3 className="text-lg font-semibold text-foreground">Aucun historique</h3>
                  <p className="text-muted-foreground mt-1">
                    Aucune notification n'a encore été envoyée
                  </p>
                </CardContent>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow className="bg-gray-50">
                      <TableHead>Date d'envoi</TableHead>
                      <TableHead>Mois concerné</TableHead>
                      <TableHead className="text-center">Alertes</TableHead>
                      <TableHead className="text-right">CA à risque</TableHead>
                      <TableHead>Destinataires</TableHead>
                      <TableHead>Envoyé par</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {notificationHistory.map((notif, i) => (
                      <TableRow key={i}>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            <Mail className="w-4 h-4 text-green-500" />
                            {new Date(notif.sent_at).toLocaleString('fr-FR', { timeZone: 'Europe/Paris' })}
                          </div>
                        </TableCell>
                        <TableCell>{notif.month}</TableCell>
                        <TableCell className="text-center">
                          <Badge variant="outline">{notif.alerts_count}</Badge>
                        </TableCell>
                        <TableCell className="text-right font-mono">
                          {formatCurrency(notif.total_at_risk)}
                        </TableCell>
                        <TableCell className="text-sm text-muted-foreground">
                          {notif.recipients?.join(', ')}
                        </TableCell>
                        <TableCell className="text-sm text-muted-foreground">
                          {notif.sent_by}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </Card>
          </TabsContent>
        </Tabs>

        {/* Settings Dialog */}
        <Dialog open={settingsOpen} onOpenChange={setSettingsOpen}>
          <DialogContent className="sm:max-w-lg">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Settings className="w-5 h-5" />
                Paramètres des notifications
              </DialogTitle>
            </DialogHeader>
            
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label htmlFor="sender_email">Adresse d'envoi</Label>
                <Input
                  id="sender_email"
                  value={settings.sender_email}
                  onChange={(e) => setSettings({ ...settings, sender_email: e.target.value })}
                  placeholder="facturation@betaye.fr"
                />
                <p className="text-xs text-muted-foreground">Doit se terminer par @betaye.fr</p>
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="recipient_email">Adresse destinataire *</Label>
                <Input
                  id="recipient_email"
                  type="email"
                  value={settings.recipient_email}
                  onChange={(e) => setSettings({ ...settings, recipient_email: e.target.value })}
                  placeholder="comptabilite@example.com"
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="cc_email">Adresse en copie (CC)</Label>
                <Input
                  id="cc_email"
                  type="email"
                  value={settings.cc_email || ''}
                  onChange={(e) => setSettings({ ...settings, cc_email: e.target.value })}
                  placeholder="direction@example.com"
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="notification_day">Jour d'envoi automatique</Label>
                <Select 
                  value={String(settings.notification_day)} 
                  onValueChange={(v) => setSettings({ ...settings, notification_day: parseInt(v) })}
                >
                  <SelectTrigger id="notification_day">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {[...Array(28)].map((_, i) => (
                      <SelectItem key={i + 1} value={String(i + 1)}>
                        Le {i + 1} de chaque mois
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-xs text-muted-foreground">
                  La notification sera envoyée automatiquement à cette date
                </p>
              </div>
              
              <div className="flex items-center justify-between pt-4 border-t">
                <div>
                  <Label htmlFor="enabled" className="font-medium">Notifications automatiques</Label>
                  <p className="text-xs text-muted-foreground">
                    Envoyer automatiquement les alertes chaque mois
                  </p>
                </div>
                <Switch
                  id="enabled"
                  checked={settings.enabled}
                  onCheckedChange={(v) => setSettings({ ...settings, enabled: v })}
                />
              </div>
            </div>
            
            <DialogFooter>
              <Button variant="outline" onClick={() => setSettingsOpen(false)}>
                Annuler
              </Button>
              <Button onClick={handleSaveSettings} disabled={savingSettings}>
                {savingSettings ? (
                  <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <CheckCircle className="w-4 h-4 mr-2" />
                )}
                Enregistrer
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </TooltipProvider>
  );
}
